import { GlobeAltIcon, WindowIcon } from '@heroicons/react/24/outline';

import { TableActions } from '@/components/table/types';
import { useBulkCapabilityJob } from '@/hooks/useCapability';
import { useEntitlements } from '@/hooks/useEntitlements';
import { useUpdatePreseed } from '@/hooks/usePreseed';
import { useUpdateSeed } from '@/hooks/useSeeds';
import { ActionOptions } from '@/sections/seeds/config';
import { SeedType } from '@/sections/seeds/types';
import { Seed, SeedStatus } from '@/types';

/**
 * Row actions configuration for seed table
 *
 * Provides type-specific action menu configurations for the seeds table.
 * Handles both web application-specific actions (web crawl, web scan) and
 * standard seed actions (approve, reject, freeze, delete, comment).
 */
export interface SeedTableRowActions {
  getRowActions: (seed: Seed) => TableActions;
}

interface UseSeedTableOptions {
  seedType: SeedType;
  onWebCrawl?: (seedKey: string) => void;
  onCommentClick?: (seed: Seed) => void;
}

/**
 * Hook for managing seed table row actions
 *
 * Provides row action menu configuration based on seed type. Supports:
 * - Web Application seeds: Web crawl, web scan, + standard actions
 * - Domain/IP seeds: Standard actions only
 *
 * Standard actions include:
 * - Approve (⌥A)
 * - Reject (⌥R)
 * - Freeze (⌥F)
 * - Add Note
 * - Delete (⌥D)
 *
 * @param options - Configuration options
 * @param options.seedType - Current seed type filter
 * @param options.onWebCrawl - Callback for web crawl action
 * @param options.onCommentClick - Callback for comment action
 *
 * @example
 * ```tsx
 * const SeedsList = () => {
 *   const [type, setType] = useState<SeedType>('domain');
 *   const modalState = useSeedModal();
 *
 *   const { getRowActions } = useSeedTable({
 *     seedType: type,
 *     onWebCrawl: (seedKey) => {
 *       modal.selectUserAgent.onAssetKeysChange([seedKey]);
 *     },
 *     onCommentClick: (seed) => {
 *       setSeedForComment(seed);
 *       modalState.comment.open();
 *     },
 *   });
 *
 *   return (
 *     <Table
 *       data={seeds}
 *       rowActions={getRowActions}
 *     />
 *   );
 * };
 * ```
 *
 * @returns Row actions configuration
 */
export const useSeedTable = ({
  seedType,
  onWebCrawl,
  onCommentClick,
}: UseSeedTableOptions): SeedTableRowActions => {
  const { canWriteAssets } = useEntitlements();
  const { mutateAsync: runJobs } = useBulkCapabilityJob();
  const { mutateAsync: updateSeed } = useUpdateSeed();
  const { mutateAsync: updatePreseed } = useUpdatePreseed();

  if (!canWriteAssets)
    return { getRowActions: () => ({ menu: { items: [] } }) };

  /**
   * Generate row actions menu for a seed
   *
   * Returns empty menu if seed is null/undefined (defensive check).
   * For web application seeds, adds web crawl and web scan actions.
   * For all seeds, includes standard approve/reject/freeze/delete/comment actions.
   */
  const getRowActions = (seed: Seed): TableActions => {
    // Defensive check: return empty menu if no seed provided
    if (!seed) {
      return {
        menu: {
          items: [],
        },
      };
    }

    // Web Application-specific actions (crawl + scan)
    const webAppActions =
      seed.class === 'webapplication'
        ? [
            {
              type: 'action' as const,
              icon: <GlobeAltIcon className="size-5" />,
              label: 'Web Crawl',
              onClick: () => {
                onWebCrawl?.(seed.key);
              },
            },
            {
              type: 'action' as const,
              icon: <WindowIcon className="size-5" />,
              label: 'Web Scan',
              onClick: async () => {
                await runJobs([
                  {
                    assetKey: seed.key,
                    capabilities: ['burp-dast-scan'],
                    config: {},
                  },
                ]);
              },
            },
            { type: 'divider' as const },
          ]
        : [];

    // Standard actions for all seed types
    const standardActions = ActionOptions.map(option => ({
      ...option,
      // Handle status update actions (approve, reject, freeze, delete)
      ...(option.type !== 'divider' &&
        option.type !== 'comment' && {
          onClick: () => {
            if (seedType === 'preseed') {
              updatePreseed({
                key: seed.key,
                status: option.status as SeedStatus,
              });
            } else {
              updateSeed({
                key: seed.key,
                identifier: seed.identifier,
                status: option.status as SeedStatus,
                type:
                  seedType === 'webapplication' ? 'webapplication' : 'asset',
              });
            }
          },
          status: undefined, // Remove status from menu item (only needed for onClick)
        }),
      // Handle comment action
      ...(option.type === 'comment' && {
        onClick: () => {
          onCommentClick?.(seed);
        },
      }),
    }));

    return {
      menu: {
        items: [...webAppActions, ...standardActions] as NonNullable<
          TableActions['menu']
        >['items'],
      },
    };
  };

  return {
    getRowActions,
  };
};
